import { useState, Suspense, lazy, useEffect, Component } from 'react'
import NavRail from './components/NavRail'
import { useHttp } from './hooks/useHttp'
import './App.css'

// ========================================================================
// LAZY-LOADED COMPONENTS - Um para cada domínio/modelo
// ========================================================================

// Home Domain
const HomePanel = lazy(() => import('./domains/home/components/HomePanel'))

// Auth Domain
const LoginPanel = lazy(() => import('./domains/admin/components/LoginPanel'))

// Sofia Domain
const ChatPanel = lazy(() => import('./domains/sofia/components/ChatPanelWrapper'))

// Booking Domain
const AppointmentsPanel = lazy(() => import('./domains/booking/components/AppointmentsPanel'))
const ReservationsPanel = lazy(() => import('./domains/booking/components/ReservationsPanel'))
const TimeBlockPanel = lazy(() => import('./domains/booking/components/TimeBlockPanel'))
const OperationalPresencePanel = lazy(() => import('./domains/booking/components/OperationalPresencePanel'))

// Packages Domain
const PackagesPanel = lazy(() => import('./domains/packages/components/PackagesPanel'))
const RecurringPanel = lazy(() => import('./domains/packages/components/RecurringPanel'))
const PromotionsPanel = lazy(() => import('./domains/packages/components/PromotionsPanel'))

// BI Domain
const BIPanel = lazy(() => import('./domains/bi/components/BIPanel'))
const FunnelDashboard = lazy(() => import('./domains/bi/components/FunnelDashboard'))

// Patients Domain
const PatientsPanel = lazy(() => import('./domains/patients/components/PatientsPanel'))

// Admin Domain
const ConfigPanel = lazy(() => import('./domains/admin/components/ConfigPanel'))
const WhatsAppPanel = lazy(() => import('./domains/admin/components/WhatsAppPanel'))

// Media Domain
const MediaPanel = lazy(() => import('./domains/media/components/MediaPanel'))

/**
 * Loading Fallback
 */
const LoadingFallback = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>Carregando...</p>
  </div>
)

/**
 * Placeholder para componentes não implementados ainda
 */
const ComingSoonPanel = ({ title }) => (
  <div className="card fade-in" style={{ padding: '40px', textAlign: 'center' }}>
    <h3 style={{ color: 'var(--ink-soft)', marginBottom: '12px' }}>🔄 {title}</h3>
    <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>
      Painel em desenvolvimento
    </p>
  </div>
)

const WHATSAPP_OFFLINE_TEXT = 'WhatsApp desconectado. O envio e o recebimento de mensagens est\u00e3o indispon\u00edveis at\u00e9 a reconex\u00e3o.'
const WHATSAPP_STATUS_UNKNOWN_TEXT = 'N\u00e3o foi poss\u00edvel verificar a disponibilidade do WhatsApp. O envio n\u00e3o deve ser considerado confirmado.'

function WhatsAppAvailabilityBanner({ http }) {
  const [status, setStatus] = useState(null)
  const [checkFailed, setCheckFailed] = useState(false)

  useEffect(() => {
    let mounted = true
    const check = async () => {
      try {
        const response = await http.get('/api/admin/whatsapp/status')
        if (mounted) {
          setStatus(response.data)
          setCheckFailed(false)
        }
      } catch {
        if (mounted) setCheckFailed(true)
      }
    }
    check()
    const timer = setInterval(check, 10000)
    return () => { mounted = false; clearInterval(timer) }
  }, [http])

  if (!checkFailed && (!status || status.connected)) return null

  return (
    <div role="alert" style={{
      margin: '0 0 16px', padding: '12px 16px', borderRadius: '10px',
      border: `1px solid ${checkFailed ? '#e0bd72' : '#e5a3a3'}`,
      background: checkFailed ? '#fff9e8' : '#fff3f3',
      color: checkFailed ? '#765514' : '#8c2424',
      fontSize: '13px', lineHeight: 1.45, width: '100%', boxSizing: 'border-box',
    }}>
      {checkFailed ? WHATSAPP_STATUS_UNKNOWN_TEXT : WHATSAPP_OFFLINE_TEXT}
    </div>
  )
}

// ========================================================================
// ERROR BOUNDARY — evita que um componente quebre a tela inteira
// ========================================================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, message: '' }
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || 'Erro desconhecido' }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="card" style={{ padding: '24px', color: 'var(--danger)', fontSize: 13, borderColor: 'var(--danger)' }}>
          <strong>Erro ao carregar painel</strong>
          <p style={{ margin: '6px 0 0', color: 'var(--ink-mute)', fontSize: 12 }}>{this.state.message}</p>
        </div>
      )
    }
    return this.props.children
  }
}

// ========================================================================
// SHARED SUB-TAB BAR
// ========================================================================

function SubTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border-soft)', padding: '0 0 0 0', marginBottom: '20px', flexShrink: 0 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            padding: '10px 18px',
            background: 'transparent',
            color: active === t.key ? 'var(--color-primary)' : 'var(--ink-soft)',
            border: 'none',
            borderBottom: active === t.key ? '2px solid var(--color-primary)' : '2px solid transparent',
            fontSize: '13px',
            fontWeight: active === t.key ? '600' : '400',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            marginBottom: '-1px',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

// ========================================================================
// MAIN APP
// ========================================================================

export default function App() {
  const [activeTab, setActiveTab] = useState('home')
  const [conversationTarget, setConversationTarget] = useState(null)
  const [biSubTab, setBiSubTab] = useState('funnel')
  const [packagesSubTab, setPackagesSubTab] = useState('packages')
  const [bookingSubTab, setBookingSubTab] = useState('appointments')
  const [adminSubTab, setAdminSubTab] = useState('config')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const http = useHttp()

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    const userId = localStorage.getItem('user_id')
    const userRole = localStorage.getItem('user_role')

    if (token && userId) {
      setIsAuthenticated(true)
      setCurrentUser({
        id: userId,
        role: userRole || 'user',
        username: localStorage.getItem('username'),
      })
    }
  }, [])

  const handleAuthenticate = (user) => {
    setCurrentUser(user)
    setIsAuthenticated(true)
  }

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_id')
    localStorage.removeItem('user_role')
    localStorage.removeItem('username')
    setIsAuthenticated(false)
    setCurrentUser(null)
    setActiveTab('sofia')
  }

  const openConversation = (phone) => {
    if (!phone) return
    sessionStorage.setItem('nora_selected_conv', phone)
    setConversationTarget(phone)
    setActiveTab('sofia')
  }

  // If not authenticated, show login panel
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <LoginPanel onAuthenticate={handleAuthenticate} />
      </Suspense>
    )
  }

  const renderContent = () => {
    switch (activeTab) {
      // HOME
      case 'home':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <HomePanel onNavigate={setActiveTab} />
          </Suspense>
        )

      // SOFIA
      case 'sofia':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ChatPanel targetConversation={conversationTarget} />
          </Suspense>
        )

      // PATIENTS
      case 'patients':
        return (
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <PatientsPanel />
            </Suspense>
          </ErrorBoundary>
        )

      // BOOKING
      case 'booking':
        return (
          <div className="booking-tab" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SubTabs
              tabs={[
                { key: 'appointments', label: 'Agendamentos' },
                { key: 'timeblocks', label: 'Bloqueios de Horário' },
                { key: 'presence', label: 'Presença Operacional' },
              ]}
              active={bookingSubTab}
              onChange={setBookingSubTab}
            />
            {bookingSubTab === 'appointments' && (
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <AppointmentsPanel onOpenConversation={openConversation} />
                </Suspense>
              </ErrorBoundary>
            )}
            {bookingSubTab === 'timeblocks' && (
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <TimeBlockPanel />
                </Suspense>
              </ErrorBoundary>
            )}
            {bookingSubTab === 'presence' && (
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <OperationalPresencePanel />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
        )

      // PACKAGES
      case 'packages':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SubTabs
              tabs={[
                { key: 'packages', label: 'Pacotes' },
                { key: 'recurring', label: 'Recorrentes' },
                { key: 'promotions', label: 'Promoções & Cupons' },
              ]}
              active={packagesSubTab}
              onChange={setPackagesSubTab}
            />
            {packagesSubTab === 'packages' && (
              <Suspense fallback={<LoadingFallback />}>
                <PackagesPanel http={http} />
              </Suspense>
            )}
            {packagesSubTab === 'recurring' && (
              <Suspense fallback={<LoadingFallback />}>
                <RecurringPanel http={http} />
              </Suspense>
            )}
            {packagesSubTab === 'promotions' && (
              <Suspense fallback={<LoadingFallback />}>
                <PromotionsPanel http={http} />
              </Suspense>
            )}
          </div>
        )

      // BI
      case 'bi':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SubTabs
              tabs={[
                { key: 'funnel', label: 'Funil de Conversão' },
                { key: 'dashboard', label: 'Dashboard BI' },
              ]}
              active={biSubTab}
              onChange={setBiSubTab}
            />
            {biSubTab === 'funnel' && (
              <Suspense fallback={<LoadingFallback />}>
                <FunnelDashboard http={http} />
              </Suspense>
            )}
            {biSubTab === 'dashboard' && (
              <Suspense fallback={<LoadingFallback />}>
                <BIPanel http={http} />
              </Suspense>
            )}
          </div>
        )

      // ADMIN
      case 'admin':
        return currentUser?.role === 'admin' ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SubTabs
              tabs={[
                { key: 'config', label: 'Configurações' },
                { key: 'whatsapp', label: 'WhatsApp' },
              ]}
              active={adminSubTab}
              onChange={setAdminSubTab}
            />
            {adminSubTab === 'config' && (
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <ConfigPanel http={http} currentUser={currentUser} />
                </Suspense>
              </ErrorBoundary>
            )}
            {adminSubTab === 'whatsapp' && (
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <WhatsAppPanel http={http} />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
        ) : (
          <ComingSoonPanel title="Acesso Restrito" />
        )

      // MEDIA
      case 'media':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <MediaPanel http={http} />
          </Suspense>
        )

      default:
        return <ComingSoonPanel title="Painel não encontrado" />
    }
  }

  return (
    <div className="app">
      {/* NAV RAIL - SIDEBAR */}
      <NavRail
        activeTab={activeTab}
        onTabChange={setActiveTab}
        currentUser={currentUser}
        onLogout={handleLogout}
      />

      {/* MAIN CONTENT AREA */}
      <div className="app-content">
        <WhatsAppAvailabilityBanner http={http} />
        {renderContent()}
      </div>
    </div>
  )
}
